import { useMemo, useState } from "react";
import EmptyState from "@/components/base/EmptyState";
import ExportMenu from "@/components/base/ExportMenu";
import { Select } from "@/components/base/FormControls";
import StatCard from "@/pages/proprietaire/components/StatCard";
import { useBiens } from "@/pages/proprietaire/biens/BiensProvider";
import { useContrats } from "@/pages/proprietaire/contrats/ContratsProvider";
import { useDepenses } from "@/pages/proprietaire/depenses/DepensesProvider";
import { DEPENSE_CATEGORIE_ICONS, DEPENSE_CATEGORIE_LABELS, type DepenseCategorie } from "@/pages/proprietaire/depenses/types";
import { useImmeubles } from "@/pages/proprietaire/immeubles/ImmeublesProvider";
import { addMonthsKey, monthLabel, periodRange, toMonthKey } from "@/pages/proprietaire/loyers/periods";
import { LOYER_STATUT_LABELS, isImpaye, type LoyerStatut } from "@/pages/proprietaire/loyers/types";
import { useLoyers } from "@/pages/proprietaire/loyers/LoyersProvider";
import { usePaiements } from "@/pages/proprietaire/paiements/PaiementsProvider";
import type { ExportColumn } from "@/utils/export";
import { formatFcfa } from "@/utils/format";
import RevenueTrendChart, { type TrendPoint } from "./components/RevenueTrendChart";

type PeriodeKey = "3m" | "6m" | "12m" | "tout";

const periodeOptions = [
  { value: "3m", label: "3 derniers mois" },
  { value: "6m", label: "6 derniers mois" },
  { value: "12m", label: "12 derniers mois" },
  { value: "tout", label: "Tout l'historique" },
];

interface RepartitionItem {
  key: string;
  label: string;
  icon: string;
  montant: number;
  count: number;
}

function Repartition({ title, items, emptyLabel }: { title: string; items: RepartitionItem[]; emptyLabel: string }) {
  const max = items.reduce((acc, item) => Math.max(acc, item.montant), 0);
  return (
    <section className="flex flex-col rounded-lg border border-background-200 bg-background-50">
      <header className="flex items-center gap-2.5 border-b border-background-200 px-4 py-3.5 md:px-5">
        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-secondary-50 text-secondary-600">
          <i className="ri-bar-chart-2-line text-base" aria-hidden="true" />
        </span>
        <h3 className="font-heading text-sm font-semibold text-foreground-950 md:text-base">{title}</h3>
      </header>
      <div className="flex flex-col gap-3.5 p-4 md:p-5">
        {items.length === 0 && <p className="text-sm text-foreground-500">{emptyLabel}</p>}
        {items.map((item) => {
          const pct = max > 0 ? Math.round((item.montant / max) * 100) : 0;
          return (
            <div key={item.key} className="flex flex-col gap-1.5">
              <div className="flex items-center justify-between gap-3">
                <span className="flex min-w-0 items-center gap-2 text-sm text-foreground-800">
                  <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-background-100 text-foreground-500">
                    <i className={`${item.icon} text-sm`} aria-hidden="true" />
                  </span>
                  <span className="truncate">{item.label}</span>
                </span>
                <span className="shrink-0 text-sm font-label font-medium text-foreground-950">
                  {formatFcfa(item.montant)}
                </span>
              </div>
              <div className="h-1.5 w-full overflow-hidden rounded-full bg-background-100">
                <div className="h-full rounded-full bg-primary-500" style={{ width: `${pct}%` }} aria-hidden="true" />
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}

export default function StatistiquesPage() {
  const { loyers, stats: loyersStats } = useLoyers();
  const { paiements, stats: paiementsStats } = usePaiements();
  const { depenses } = useDepenses();
  const { biens } = useBiens();
  const { immeubles } = useImmeubles();
  const { getContrat } = useContrats();

  const [periodeFilter, setPeriodeFilter] = useState<PeriodeKey>("6m");
  const [immeubleFilter, setImmeubleFilter] = useState("");

  const immeubleOptions = useMemo(
    () => [{ value: "", label: "Tous les immeubles" }, ...immeubles.map((i) => ({ value: i.id, label: i.nom }))],
    [immeubles]
  );

  const months = useMemo<string[]>(() => {
    const current = toMonthKey(new Date());
    if (!current) return [];
    if (periodeFilter === "tout") {
      const keys = new Set<string>();
      paiements.forEach((p) => keys.add(p.periode));
      depenses.forEach((d) => keys.add(toMonthKey(new Date(d.date))));
      const sorted = Array.from(keys).filter(Boolean).sort();
      if (sorted.length === 0) {
        const fallback: string[] = [];
        for (let i = 5; i >= 0; i -= 1) fallback.push(addMonthsKey(current, -i));
        return fallback.filter(Boolean);
      }
      return periodRange(sorted[0], current, 240);
    }
    const count = periodeFilter === "12m" ? 12 : periodeFilter === "6m" ? 6 : 3;
    const list: string[] = [];
    for (let i = count - 1; i >= 0; i -= 1) {
      const key = addMonthsKey(current, -i);
      if (key) list.push(key);
    }
    return list;
  }, [periodeFilter, paiements, depenses]);

  const monthSet = useMemo(() => new Set(months), [months]);
  const inRange = (key: string) => monthSet.size === 0 || monthSet.has(key);

  // Paiements : l'immeuble se déduit du contrat rattaché.
  const filteredPaiements = useMemo(
    () =>
      paiements.filter((paiement) => {
        if (!inRange(paiement.periode)) return false;
        if (!immeubleFilter) return true;
        const contrat = getContrat(paiement.contratId);
        return contrat?.immeubleId === immeubleFilter;
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [paiements, immeubleFilter, monthSet, getContrat]
  );

  const filteredLoyers = useMemo(
    () =>
      loyers.filter(
        (loyer) => inRange(loyer.periode) && (!immeubleFilter || loyer.immeubleId === immeubleFilter)
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [loyers, immeubleFilter, monthSet]
  );

  const filteredDepenses = useMemo(
    () =>
      depenses.filter(
        (depense) =>
          inRange(toMonthKey(new Date(depense.date))) &&
          (!immeubleFilter || depense.immeubleId === immeubleFilter)
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [depenses, immeubleFilter, monthSet]
  );

  const filteredBiens = useMemo(
    () => biens.filter((bien) => !immeubleFilter || bien.immeubleId === immeubleFilter),
    [biens, immeubleFilter]
  );

  const revenusLocatifs = filteredPaiements.reduce((sum, item) => sum + item.montant, 0);
  const depensesTotal = filteredDepenses.reduce((sum, item) => sum + item.montant, 0);
  const loyersPayesMontant = filteredLoyers
    .filter((loyer) => loyer.statut === "paye")
    .reduce((sum, loyer) => sum + loyer.montant, 0);
  const loyersImpayesMontant = filteredLoyers
    .filter((loyer) => isImpaye(loyer.statut))
    .reduce((sum, loyer) => sum + loyer.reste, 0);
  const resultatNet = revenusLocatifs - depensesTotal;

  const biensOccupes = filteredBiens.filter((bien) => bien.statut === "occupe").length;
  const biensDisponibles = filteredBiens.filter((bien) => bien.statut === "disponible").length;
  const tauxOccupation = filteredBiens.length > 0 ? Math.round((biensOccupes / filteredBiens.length) * 100) : 0;

  const trend: TrendPoint[] = useMemo(
    () =>
      months.map((month) => ({
        key: month,
        label: monthLabel(month).slice(0, 3),
        revenus: filteredPaiements
          .filter((paiement) => paiement.periode === month)
          .reduce((sum, item) => sum + item.montant, 0),
        depenses: filteredDepenses
          .filter((depense) => toMonthKey(new Date(depense.date)) === month)
          .reduce((sum, item) => sum + item.montant, 0),
      })),
    [months, filteredPaiements, filteredDepenses]
  );

  const repartitionLoyers: RepartitionItem[] = useMemo(() => {
    const statuts: LoyerStatut[] = ["paye", "a-venir", "en-retard", "impaye"];
    const icons: Record<LoyerStatut, string> = {
      paye: "ri-checkbox-circle-line",
      "a-venir": "ri-calendar-schedule-line",
      "en-retard": "ri-timer-line",
      impaye: "ri-alert-line",
    };
    return statuts
      .map((statut) => {
        const items = filteredLoyers.filter((loyer) => loyer.statut === statut);
        return {
          key: statut,
          label: LOYER_STATUT_LABELS[statut],
          icon: icons[statut],
          montant: items.reduce((sum, loyer) => sum + loyer.montant, 0),
          count: items.length,
        };
      })
      .filter((item) => item.count > 0)
      .sort((a, b) => b.montant - a.montant);
  }, [filteredLoyers]);

  const repartitionDepenses: RepartitionItem[] = useMemo(() => {
    const totals = new Map<DepenseCategorie, { montant: number; count: number }>();
    filteredDepenses.forEach((depense) => {
      const current = totals.get(depense.categorie) ?? { montant: 0, count: 0 };
      totals.set(depense.categorie, { montant: current.montant + depense.montant, count: current.count + 1 });
    });
    return Array.from(totals.entries())
      .map(([categorie, value]) => ({
        key: categorie,
        label: DEPENSE_CATEGORIE_LABELS[categorie],
        icon: DEPENSE_CATEGORIE_ICONS[categorie],
        montant: value.montant,
        count: value.count,
      }))
      .sort((a, b) => b.montant - a.montant);
  }, [filteredDepenses]);

  const hasData = paiements.length > 0 || depenses.length > 0 || loyers.length > 0;

  const exportRows = [
    { label: "Revenus encaissés", value: formatFcfa(revenusLocatifs) },
    { label: "Loyers payés", value: formatFcfa(loyersPayesMontant) },
    { label: "Loyers impayés", value: formatFcfa(loyersImpayesMontant) },
    { label: "Dépenses", value: formatFcfa(depensesTotal) },
    { label: "Taux d'occupation", value: `${tauxOccupation} %` },
    { label: "Biens disponibles", value: String(biensDisponibles) },
    { label: "Biens occupés", value: String(biensOccupes) },
    { label: "Résultat net", value: formatFcfa(resultatNet) },
    { label: "Total encaissé (historique)", value: formatFcfa(paiementsStats.montantTotal) },
    { label: "Ce mois-ci", value: formatFcfa(paiementsStats.ceMois) },
    { label: "Montant des loyers impayés", value: formatFcfa(loyersStats.montantImpayes) },
  ];
  const exportColumns: ExportColumn<{ label: string; value: string }>[] = [
    { header: "Indicateur", value: (row) => row.label },
    { header: "Valeur", value: (row) => row.value },
  ];

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
        <div className="max-w-2xl">
          <h2 className="font-heading text-xl font-semibold text-foreground-950 md:text-2xl">Statistiques</h2>
          <p className="mt-1.5 text-sm leading-relaxed text-foreground-600">
            Une vue d'ensemble de vos revenus locatifs, de vos impayés, de vos dépenses et de votre taux d'occupation.
            Tous les montants sont calculés à partir des données réellement présentes dans vos modules.
          </p>
        </div>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:w-auto lg:shrink-0">
          <div className="flex flex-col gap-1.5">
            <label htmlFor="stat-periode" className="text-xs font-label font-medium text-foreground-700">
              Période
            </label>
            <Select
              id="stat-periode"
              value={periodeFilter}
              onChange={(event) => setPeriodeFilter(event.target.value as PeriodeKey)}
              options={periodeOptions}
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <label htmlFor="stat-immeuble" className="text-xs font-label font-medium text-foreground-700">
              Immeuble
            </label>
            <Select
              id="stat-immeuble"
              value={immeubleFilter}
              onChange={(event) => setImmeubleFilter(event.target.value)}
              options={immeubleOptions}
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <span className="text-xs font-label font-medium text-foreground-700">Export des indicateurs</span>
            <ExportMenu
              rows={exportRows}
              columns={exportColumns}
              filename="statistiques"
              title="Statistiques"
              className="[&>button]:w-full"
            />
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <StatCard icon="ri-line-chart-line" label="Revenus encaissés" value={formatFcfa(revenusLocatifs)} tone="primary" />
        <StatCard icon="ri-checkbox-circle-line" label="Loyers payés" value={formatFcfa(loyersPayesMontant)} tone="secondary" />
        <StatCard icon="ri-alert-line" label="Loyers impayés" value={formatFcfa(loyersImpayesMontant)} tone="accent" />
        <StatCard icon="ri-money-cny-circle-line" label="Dépenses" value={formatFcfa(depensesTotal)} tone="neutral" />
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <StatCard icon="ri-pie-chart-line" label="Taux d'occupation" value={`${tauxOccupation} %`} tone="primary" hint={`${biensOccupes}/${filteredBiens.length} bien(s) occupé(s)`} />
        <StatCard icon="ri-home-4-line" label="Biens disponibles" value={String(biensDisponibles)} tone="secondary" />
        <StatCard icon="ri-home-smile-line" label="Biens occupés" value={String(biensOccupes)} tone="accent" />
        <StatCard
          icon="ri-scales-3-line"
          label="Résultat net"
          value={formatFcfa(resultatNet)}
          tone={resultatNet < 0 ? "accent" : "neutral"}
          hint="Revenus encaissés − dépenses"
        />
      </div>

      {!hasData && (
        <EmptyState
          icon="ri-bar-chart-box-line"
          title="Aucune donnée à analyser"
          description="Créez des immeubles et des biens, puis enregistrez des contrats, des paiements et des dépenses : vos statistiques se construiront automatiquement."
        />
      )}

      {hasData && (
        <section className="flex flex-col rounded-lg border border-background-200 bg-background-50">
          <header className="flex items-center gap-2.5 border-b border-background-200 px-4 py-3.5 md:px-5">
            <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-accent-50 text-accent-700">
              <i className="ri-line-chart-line text-base" aria-hidden="true" />
            </span>
            <div>
              <h3 className="font-heading text-sm font-semibold text-foreground-950 md:text-base">
                Évolution des revenus
              </h3>
              <p className="text-xs text-foreground-500">Revenus encaissés et dépenses par mois.</p>
            </div>
          </header>
          <div className="p-4 md:p-5">
            <RevenueTrendChart data={trend} />
          </div>
        </section>
      )}

      {hasData && (
        <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
          <Repartition
            title="Répartition des loyers par statut"
            items={repartitionLoyers}
            emptyLabel="Aucun loyer sur la période sélectionnée."
          />
          <Repartition
            title="Dépenses par catégorie"
            items={repartitionDepenses}
            emptyLabel="Aucune dépense sur la période sélectionnée."
          />
        </div>
      )}

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <div className="rounded-lg border border-background-200 bg-background-50 p-4 md:p-5">
          <p className="text-xs text-foreground-500">Total encaissé (historique)</p>
          <p className="mt-1.5 font-heading text-lg font-semibold text-foreground-950">
            {formatFcfa(paiementsStats.montantTotal)}
          </p>
          <p className="mt-0.5 text-xs text-foreground-400">{paiementsStats.total} paiement(s)</p>
        </div>
        <div className="rounded-lg border border-background-200 bg-background-50 p-4 md:p-5">
          <p className="text-xs text-foreground-500">Ce mois-ci</p>
          <p className="mt-1.5 font-heading text-lg font-semibold text-foreground-950">
            {formatFcfa(paiementsStats.ceMois)}
          </p>
          <p className="mt-0.5 text-xs text-foreground-400">Encaissements du mois en cours</p>
        </div>
        <div className="rounded-lg border border-background-200 bg-background-50 p-4 md:p-5">
          <p className="text-xs text-foreground-500">Montant des loyers impayés</p>
          <p className="mt-1.5 font-heading text-lg font-semibold text-foreground-950">
            {formatFcfa(loyersStats.montantImpayes)}
          </p>
          <p className="mt-0.5 text-xs text-foreground-400">
            {loyersStats.enRetard + loyersStats.impayes} loyer(s) en retard ou impayé(s)
          </p>
        </div>
      </div>
    </div>
  );
}